import { Skeleton } from '@atgfr8/atg-ui-tailwind';
import { getV3Route, tryGetRoute } from '@components/Sidebar/helpers';
import { userRoles } from '@constants/common';
import { RoutingFlags } from '@constants/routingFlags';
import { openFeedbackPanel } from '@store/feedback';
import { openModal } from '@store/modals';
import {
  customResetStore,
  resetCarrierStore,
  resetCustomerStore,
} from '@store/orders';
import { UserRole, userStore } from '@store/user';
import { getCanUseV3RouteInjection } from '@utils/envarUtility';
import { isRoutingFlagEnabled } from '@utils/routingFlagUtils';
import {
  hasPermission,
  isAdmin,
  isCapacity,
  isSuperAdmin,
  userHasRole,
} from '@utils/utils';
import { ApCallingQueueRoute } from '@views/apCallingQueue';
import { BulkOrderUploadRoute } from '@views/bulkOrderUpload/index';
// import { ArCallingQueueRoute } from '@views/arCallingQueue';
import {
  CreditDashboardRoute,
  OfficeCreditApplicationMetricsRoute,
  CreditApplicationsRoute,
  CreditMyApplicationsRoute,
  CreditQueueRoute,
  CreditScoringRoute,
} from '@views/creditApplications';
import {
  canViewCreditAdminPages,
  canViewOfficeCreditApplicationMetrics,
} from '@views/creditApplications/creditAdminAccess';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  resetAccessorialStore,
  resetLtlQuoteState,
  resetLTLQuotingResultStore,
} from '@store/ltl/store';
import { BedrockAssistantRoute } from '@views/bedrockAssistant';
import { CapacityFinderRoute } from '@views/capacityFinder';
import { CarrierMatchingRoute } from '@views/carrierMatching';
import { CarrierRelationsQueuesRoute } from '@views/carrierRelationsQueues';
import {
  CarrierLaneSearchRoute,
  CarrierPlaylists,
  CarrierRoute,
  NewCarrierDetailsRoute,
} from '@views/carriers';
import { ClaimsTabRoute } from '@views/claims';
import { CollectionsDashboardRoute } from '@views/collectionsDashboard';
import {
  CustomersRoute,
  DormantAccountsRoute,
  NewCustomerRoute,
} from '@views/customer';
import { DocumentsPageRoute } from '@views/documentsPage';
import { DormantPoolRoute } from '@views/dormantPool';
import { EquipmentSearchRoute } from '@views/equipmentSearch';
import {
  FactoringCompanyDetailsCreationRoute,
  FactoringCompanyRoute,
} from '@views/factoringCompany';
import { FinancialAutoProcessingRoute } from '@views/financialAutoProcessing';
import { HoldsTabRoute } from '@views/holds';
import { LoadboardRoute, OldLoadsRoute } from '@views/loadboard';
import { LoadSearchRoute } from '@views/loadSearch';
import { OfficeRoute } from '@views/office';
import { PendingApprovalsTabRoute } from '@views/pendingApprovals';
import { OrderRoute } from '@views/order';
import { NewProspectRoute, ProspectRoute } from '@views/prospect';
import { RevenueDashboardRoute } from '@views/revenueDashboard';
import { ShortPayAdminRoute } from '@views/shortPayAdmin';
import { CarrierBlacklistReportRoute } from '@views/carrierBlacklistReport';
import {
  NewRevenueSearchRoute,
  RevenueSearchRoute,
} from '@views/revenueSearch';
import { SettlementDashboardRoute } from '@views/settlementDashboard';
import { TrackTraceRoute } from '@views/trackTrace';
import { NewVendorsDetailsRoute, VendorsRoute } from '@views/vendors';
import { createMemo, createResource, For, Show } from 'solid-js';

import MainMenuItem from './MainMenuItem';
import { NavLink } from './NavLink';
import NavOverflowMenu from './NavOverflowMenu';

const OVERFLOW_NAV_IDS = new Set(['credit', 'help', 'test']);

export default function NavBar() {
  const [isPostingAvailable] = createResource(async () => {
    // Check if user has a PostingGroupId
    await userStore.userPromise;
    return Boolean(userStore.user.postingGroupId);
  });

  const [isDormantPoolEnabled] = createResource(async () => {
    // Check dormant customer enabled flag from the user
    await userStore.userPromise;
    return Boolean(userStore.user.dormantCustomerEnabled);
  });

  const isRoutingFlagEnabledMemo = (
    flag: RoutingFlags,
    value: string = 'Enabled',
  ) => createMemo(() => isRoutingFlagEnabled(flag, value));

  const navlinks: Array<NavLink> = [
    {
      id: 'dashboard',
      label: 'Reports',
      hide: () => isCapacity(),
      children: [
        {
          path: RevenueDashboardRoute.path,
          label: 'Revenue Dashboard',
          hide: () => {
            return (
              Boolean(hasPermission('Hide Dashboard')) &&
              !Boolean(hasPermission('View Settlements'))
            );
          },
        },
        {
          path: SettlementDashboardRoute.path,
          label: 'Settlements Dashboard',
          hide: () => {
            return !Boolean(hasPermission('View Settlements')) && !isAdmin();
          },
        },
        {
          path: RevenueSearchRoute.path,
          label: 'Revenue Search',
          hide: () => {
            return (
              isCapacity() ||
              isFeatureFlagEnabled(FeatureFlags.OptimizedRevenueSearch)()
            );
          },
        },
        {
          path: NewRevenueSearchRoute.path,
          label: 'Revenue Search',
          hide: () => {
            return (
              isCapacity() ||
              !isFeatureFlagEnabled(FeatureFlags.OptimizedRevenueSearch)()
            );
          },
        },
        {
          path: CollectionsDashboardRoute.path,
          label: 'Collections Dashboard',
          hide: () => userHasRole('Accounting') || userHasRole('Admin'),
        },
        {
          path: CarrierBlacklistReportRoute.path,
          label: 'Carrier Blacklist',
          hide: () =>
            !isFeatureFlagEnabled(FeatureFlags.CarrierBlacklistReport)(),
        },
      ],
    },
    {
      id: 'loads',
      label: 'Loads',
      children: [
        {
          path: LoadboardRoute.path,
          label: 'Loadboard',
        },
        {
          path: TrackTraceRoute.path,
          label: 'Track & Trace',
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.TrackTrace)(),
        },
        {
          path: LoadSearchRoute.path,
          label: 'Load Search',
        },
        {
          path: PendingApprovalsTabRoute.path,
          label: 'Resolution Queue',
          hide: () => !isFeatureFlagEnabled(FeatureFlags.PendingApprovalsV2)(),
        },
        {
          path: '/load/tenders',
          label: 'EDI Load Tenders',
          hide: () => isCapacity(),
        },
        {
          path: ClaimsTabRoute.path,
          label: 'Claims',
        },
        {
          path: HoldsTabRoute.path,
          label: 'Holds',
        },
        {
          path: OldLoadsRoute.path,
          label: 'Old Loads',
          seperator: true,
          hide: () => isFeatureFlagEnabled(FeatureFlags.PendingApprovalsV2)(),
        },
        {
          path: BulkOrderUploadRoute.path,
          label: 'Bulk Order Upload',
          hide: () =>
            !isFeatureFlagEnabled(FeatureFlags.ReleaseBulkOrderUploadUI)(),
        },
        {
          path: OrderRoute.path,
          label: 'New Load',
          custom() {
            customResetStore();
            resetCustomerStore();
            resetCarrierStore();
            resetLtlQuoteState();
            resetLTLQuotingResultStore();
            resetAccessorialStore();
          },
        },
      ],
    },
    {
      id: 'carriers',
      label: 'Carriers',
      children: [
        {
          path: CarrierRoute.path,
          label: 'Carriers',
        },
        {
          path: CapacityFinderRoute.path,
          label: 'Capacity Finder',
          hide: () =>
            !isRoutingFlagEnabledMemo(RoutingFlags.V4CapacityFinder)(),
        },
        {
          path: CarrierMatchingRoute.path,
          label: 'Carrier Matching',
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.CarrierMatching)(),
        },
        {
          path: '/carrier/matching/offers',
          label: 'My Offers',
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.CarrierMatching)(),
        },
        {
          path: CarrierPlaylists.path,
          label: 'Playlists',
        },
        {
          path: FactoringCompanyRoute.path,
          label: 'Factoring Companies',
          hide: () => !userHasRole(userRoles.ADMIN),
        },
        {
          path: CarrierLaneSearchRoute.path,
          label: 'Lane Search',
        },
        {
          path: CarrierRelationsQueuesRoute.path,
          label: 'Carrier Queues',
          hide: () => true,
          seperator: () => !(isPostingAvailable() ?? false),
        },
        {
          path: EquipmentSearchRoute.path,
          label: 'Equipment Search',
          hide: () => !(isPostingAvailable() ?? false),
          seperator: true,
        },
        {
          path: NewCarrierDetailsRoute.path,
          label: 'New Carrier',
        },
        {
          path: FactoringCompanyDetailsCreationRoute.path,
          label: 'New Factoring Company',
          hide: () => !userHasRole(userRoles.ADMIN),
        },
      ],
    },
    {
      id: 'vendors',
      label: 'Vendors',
      hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.VendorManagement)(),
      children: [
        {
          path: VendorsRoute.path,
          label: 'Vendors',
        },
        {
          path: NewVendorsDetailsRoute.path,
          label: 'New Vendor',
        },
      ],
    },
    {
      id: 'customers',
      label: 'Customers',
      hide: () => isCapacity(),
      children: [
        {
          path: CustomersRoute.path,
          label: 'Customers',
        },
        {
          path: '/carrier/matching/manager',
          label: 'Offer Manager',
          hide: () =>
            !isRoutingFlagEnabledMemo(
              RoutingFlags.CarrierMatchingOfferManager,
            )(),
        },
        {
          path: ProspectRoute.path,
          label: 'Prospects',
          hide: () => userHasRole(userRoles.ADMIN_LITE),
        },
        {
          path: DormantAccountsRoute.path,
          label: 'My Dormant Accounts',
          hide: () => userHasRole(userRoles.ADMIN_LITE),
        },
        {
          path: DormantPoolRoute.path,
          label: 'Dormant Pool',
          hide: () => !(isDormantPoolEnabled() ?? false),
          seperator: true,
        },
        // {
        //   path: getV3Route('/Customer/EDIQueue'),
        //   label: 'EDI Queue',
        //   seperator: true,
        //   hide: () => !userHasRole(userRoles.ADMIN),
        // },
        {
          path: NewProspectRoute.path,
          label: 'Enter Prospect Customer',
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.V4NewProspect)(),
        },
        {
          path: NewCustomerRoute.path,
          label: 'Enter New Customer',
        },
      ],
    },
    {
      id: 'admin',
      label: 'Admin',
      children: [
        {
          path: getV3Route('/Accounting/AdminTools'),
          label: 'Tools',
          hide: () => !userHasRole(UserRole.Admin),
        },
        {
          path: ShortPayAdminRoute.path,
          label: 'Short Pay Admin',
          hide: () => !isFeatureFlagEnabled(FeatureFlags.ShortPayAdmin)(),
        },
        {
          path: '/feature-flags',
          label: 'Feature Flag Management',
          hide: () =>
            !isRoutingFlagEnabledMemo(RoutingFlags.FeatureFlagManagement)(),
        },
        {
          path: '/feature-flag-explorer',
          label: 'Feature Flag Explorer',
          hide: () =>
            !isRoutingFlagEnabledMemo(RoutingFlags.FeatureFlagManagement)(),
        },
        {
          path: '/feature-flags/new',
          label: 'Feature Flag Portal',
          hide: () =>
            !isFeatureFlagEnabled(FeatureFlags.ReleaseNewFeatureFlagPortal)(),
        },
        {
          path: '/helpdesk/order-access',
          label: 'Helpdesk Operations',
          hide: () => !isFeatureFlagEnabled(FeatureFlags.HelpdeskSelfService)(),
        },
        {
          path: '/jira-ticket-metrics',
          label: 'ATG PDLC Metrics',
          hide: () =>
            !(isAdmin() || isSuperAdmin()) ||
            !isFeatureFlagEnabled(FeatureFlags.JiraMetricsService)(),
        },
        {
          path: '/Maxbuy',
          label: 'Max Buy Thresholds',
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.MaxBuy)(),
        },
        {
          path: '/user/details',
          label: 'My Account',
        },
        {
          path: DocumentsPageRoute.path,
          label: 'Documents',
        },
        {
          path: '/office/details',
          label: 'Manage Office',
          hide: () => !userHasRole('Manager'),
        },
        {
          path: BedrockAssistantRoute.path,
          label: 'Bedrock Assistant',
          hide: () => !isFeatureFlagEnabled(FeatureFlags.BedrockAssistant)(),
        },
        {
          path: OfficeRoute.path,
          label: 'All Offices',
          hide: () => !userHasRole('Recruiter') && !userHasRole(UserRole.Admin),
        },
      ],
    },
    {
      id: 'accounting',
      label: 'Accounting',
      hide: () => !userHasRole(userRoles.ADMIN),
      children: [
        {
          path: getV3Route('/Accounting/Invoicing'),
          label: 'Invoicing',
        },
        {
          path: getV3Route('/Accounting/BillingQueue'),
          label: 'Billing Queues',
        },
        {
          path: ApCallingQueueRoute.path,
          label: 'AP Calling Queue',
        },
        // {
        //   path: getV3Route('/Accounting/Groups'),
        //   label: 'Manage Accounting Groups',
        // },
        {
          path: getV3Route('/Accounting/EFSHistory'),
          label: 'EFS History',
        },
        {
          path: FinancialAutoProcessingRoute.path,
          label: 'Financial Auto Processing',
          seperator: true,
        },
        {
          path: getV3Route('/Accounting/Collections'),
          label: 'Collections',
          seperator: true,
        },
        // {
        //   path: getV3Route('/Accounting/CreditInsurance'),
        //   label: 'Credit Insurance',
        // },
        // {
        //   path: ArCallingQueueRoute.path,
        //   label: 'AR Calling Queue',
        //   seperator: true,
        // },
        {
          path: getV3Route('/Accounting/Settlements'),
          label: 'Run Settlements',
          seperator: true,
        },
        {
          path: getV3Route('/Accounting/UploadTool'),
          label: 'Upload Tool',
        },
      ],
    },
    {
      id: 'credit',
      label: 'Credit',
      hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.CreditApp)(),
      children: [
        {
          path: CreditDashboardRoute.path,
          label: 'Dashboard',
        },
        {
          path: OfficeCreditApplicationMetricsRoute.path,
          label: 'Office Credit Application Metrics',
          hide: () => !canViewOfficeCreditApplicationMetrics(),
        },
        {
          path: CreditMyApplicationsRoute.path,
          label: 'My Applications',
        },
        {
          path: CreditApplicationsRoute.path,
          label: 'Applications',
          hide: () => !canViewCreditAdminPages(),
        },
        {
          path: CreditQueueRoute.path,
          label: 'Credit Queues',
          hide: () => !canViewCreditAdminPages(),
        },
        {
          path: CreditScoringRoute.path,
          hide: () => !isRoutingFlagEnabledMemo(RoutingFlags.CreditScoring)(),
          label: 'Scoring Admin',
        },
      ],
    },
    {
      id: 'help',
      label: 'Help',
      children: [
        {
          path: '/application/orgchart',
          label: 'Contact Directory',
          externalLink: true,
        },
        {
          path: 'https://atg-merch.printful.me/',
          label: 'ATG Store - Merchandise',
          externalLink: true,
        },
        {
          path: 'https://armstrong.ourproshop.com/',
          label: 'ATG Store - Marketing',
          externalLink: true,
          seperator: true,
        },
        {
          path: 'http://media.armstrongtransport.com',
          label: 'Media Portal',
          externalLink: true,
        },
        {
          path: 'https://armstrongacademy.talentlms.com/',
          label: 'Training | Armstrong Academy',
          externalLink: true,
          hide: () => !(isAdmin() || isSuperAdmin()),
        },
        {
          path: 'https://agents-armstrongacademy.talentlms.com/',
          label: 'Training | Armstrong Academy',
          externalLink: true,
          hide: () => Boolean(isAdmin() || isSuperAdmin()),
        },
        {
          path: 'https://armstrongtransport.sharepoint.com/sites/ArmstrongAcademyResourceCenter/SitePages/LearningTeamHome.aspx?ga=1',
          label: 'Armstrong Resource Center',
          externalLink: true,
          hide: () =>
            !Boolean(
              // We'RE CHECKING ATG CORP OFFICE AND ALL ATG-COSTORE OFFICE IDS HERE
              [63, 774252, 774173, 774167, 144, 72].includes(
                userStore.user.officeId,
              ),
            ),
        },
        // {
        //   path: getV3Route('/EDI/FAQ'),
        //   label: 'EDI FAQ',
        // },
        // {
        //   path: 'mailto:product@armstrongtransport.com?Subject=Ideas and Feedback',
        //   label: 'Ideas and Feedback',
        //   mailto: true,
        // },
      ],
    },
    {
      id: 'test',
      label: 'Support',
      path: '',
      children: [
        {
          path: '',
          label: 'Request Support',
          custom: () => {
            openModal('SupportModal');
          },
        },
        {
          path: '',
          label: 'Send Feedback',
          hide: () =>
            !isFeatureFlagEnabled(FeatureFlags.ReleaseProductFeedback)(),
          custom: () => {
            openFeedbackPanel('Idea');
          },
        },
      ],
    },
  ];

  // Filter navlinks with hidden items removed - make this reactive with createMemo
  const visibleNavlinks = () => {
    return navlinks
      .filter((navlink) => !(navlink.hide?.() ?? false))
      .map((navlink) => ({
        ...navlink,
        children: navlink.children?.filter(
          (child) => !(child.hide?.() ?? false),
        ),
      }));
  };

  const primaryNavlinks = () =>
    visibleNavlinks().filter((navlink) => !OVERFLOW_NAV_IDS.has(navlink.id));

  const overflowNavlinks = () =>
    visibleNavlinks()
      .filter((navlink) => OVERFLOW_NAV_IDS.has(navlink.id))
      .map((navlink) => getNavlink(navlink));

  const getNavlink = (navLink: NavLink) => {
    const processedNavLink = { ...navLink };

    if (!getCanUseV3RouteInjection()) {
      return processedNavLink;
    }

    if (processedNavLink.children) {
      // Filter out hidden children
      processedNavLink.children = processedNavLink.children
        .filter((child) => !(child.hide ? child.hide() : false))
        .map((child) => {
          if (child.path.includes('mailto:')) {
            return child;
          }
          const childPath = tryGetRoute(child.path).route;
          return { ...child, path: childPath };
        });
    } else if (Boolean(processedNavLink.path)) {
      const routeInfo = tryGetRoute(processedNavLink.path);
      processedNavLink.path = routeInfo.route;
    }

    return processedNavLink;
  };

  const NavBarSkeleton = () => (
    <div class="flex flex-wrap">
      <For each={[1, 2, 3, 4, 5]}>
        {() => (
          <div class="flex items-center h-full border-b-4 border-transparent">
            <div class="py-2 px-4">
              <Skeleton
                variant="rectangular"
                width={80}
                height={35}
                class="bg-[#045E86]!"
              />
            </div>
          </div>
        )}
      </For>
    </div>
  );

  return (
    <Show when={!userStore.isLoading} fallback={<NavBarSkeleton />}>
      <div class="flex items-center flex-wrap justify-start">
        <For each={primaryNavlinks()}>
          {(navlink) => (
            <div class="flex items-center border-b-4 border-transparent hover:border-white hover:bg-[#0a2634] shrink-0">
              <MainMenuItem navlink={getNavlink(navlink)} />
            </div>
          )}
        </For>
        <For each={overflowNavlinks()}>
          {(navlink) => (
            <div class="flex max-[1800px]:hidden! items-center border-b-4 border-transparent hover:border-white hover:bg-[#0a2634] shrink-0">
              <MainMenuItem navlink={navlink} />
            </div>
          )}
        </For>
        <Show when={overflowNavlinks().length > 0}>
          <div class="hidden max-[1800px]:flex!">
            <NavOverflowMenu navlinks={overflowNavlinks()} />
          </div>
        </Show>
      </div>
    </Show>
  );
}
